import { useState, useMemo } from 'react';
import { getUnresolvedConflicts, resolveConflict, clearConflicts, type ConflictInfo as ConflictInfoType } from '@/lib/syncQueue';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onResolved: () => void;
}

const CONFLICT_TYPE_LABELS: Record<string, string> = {
  REFERENCED_BY_PENDING: 'Référence bloquante',
  TARGET_DELETED: 'Cible supprimée',
  TARGET_MODIFIED: 'Cible modifiée',
  FOREIGN_KEY_VIOLATION: 'Contrainte de base',
  DUPLICATE: 'Doublon',
  SERVER_ERROR: 'Erreur serveur',
  TIME_SLOT_CONFLICT: 'Créneau déjà pris',
  TIME_SLOT_OVERLAP: 'Chevauchement horaire',
  PATIENT_OVERLAP: 'Conflit patient',
  TIME_SLOT_TAKEN: 'Créneau réservé',
  RLS_BLOCKED: 'Accès refusé (RLS)',
  PARENT_FAILED: 'Dépendance rompue',
  CROSS_DEVICE_CONFLICT: 'Conflit multi-appareils',
};

const CONFLICT_TYPE_COLORS: Record<string, string> = {
  REFERENCED_BY_PENDING: 'bg-amber-100 text-amber-800',
  TARGET_DELETED: 'bg-red-100 text-red-800',
  TARGET_MODIFIED: 'bg-orange-100 text-orange-800',
  FOREIGN_KEY_VIOLATION: 'bg-amber-100 text-amber-800',
  DUPLICATE: 'bg-sky-100 text-sky-800',
  SERVER_ERROR: 'bg-red-100 text-red-800',
  TIME_SLOT_CONFLICT: 'bg-rose-100 text-rose-800',
  TIME_SLOT_OVERLAP: 'bg-orange-100 text-orange-800',
  PATIENT_OVERLAP: 'bg-violet-100 text-violet-800',
  TIME_SLOT_TAKEN: 'bg-red-100 text-red-800',
  RLS_BLOCKED: 'bg-slate-200 text-slate-800',
  PARENT_FAILED: 'bg-gray-100 text-gray-700',
  CROSS_DEVICE_CONFLICT: 'bg-red-100 text-red-800',
};

const TABLE_LABELS: Record<string, string> = {
  medecins: 'Médecin',
  patients: 'Patient',
  secretaires: 'Secrétaire',
  rendez_vous: 'Rendez-vous',
  consultations: 'Consultation',
  prescriptions: 'Prescription',
  dossiers_analyses: 'Dossier d\'analyses',
  resultats_analyses: 'Résultat',
  suivis_patients: 'Suivi patient',
  bons_laboratoire: 'Bon de laboratoire',
  disponibilites_medecins: 'Créneau',
  medicaments: 'Médicament',
  actualites: 'Actualité',
  laborantins: 'Laborantin',
  sage_femmes: 'Sage-femme',
  infirmiers: 'Infirmier',
  comptables: 'Comptable',
  fiches_surveillance: 'Fiche de surveillance',
};

const OPERATION_LABELS: Record<string, string> = {
  INSERT: 'Création',
  UPDATE: 'Modification',
  DELETE: 'Suppression',
};

export default function ConflictResolutionModal({ isOpen, onClose, onResolved }: Props) {
  const [conflicts, setConflicts] = useState<ConflictInfoType[]>(getUnresolvedConflicts());
  const [resolving, setResolving] = useState<Record<string, boolean>>({});
  const [showCloseWarning, setShowCloseWarning] = useState(false);

  // Détecter les conflits critiques qui concernent des rendez-vous
  const rdvConflicts = useMemo(() => {
    return conflicts.filter(
      (c) =>
        (c.table === 'rendez_vous' &&
        ['TIME_SLOT_CONFLICT', 'TIME_SLOT_OVERLAP', 'TIME_SLOT_TAKEN', 'PATIENT_OVERLAP'].includes(c.type)) ||
        ['CROSS_DEVICE_CONFLICT'].includes(c.type),
    );
  }, [conflicts]);

  if (!isOpen) return null;

  const handleResolve = async (conflictId: string, resolution: 'RETRY' | 'DISCARD' | 'FORCE') => {
    setResolving((prev) => ({ ...prev, [conflictId]: true }));
    try {
      await resolveConflict(conflictId, resolution);
      setConflicts(getUnresolvedConflicts());
    } catch {
      // Silencieux
    } finally {
      setResolving((prev) => ({ ...prev, [conflictId]: false }));
    }
  };

  const handleDismissAll = () => {
    clearConflicts();
    setConflicts([]);
    onResolved();
    onClose();
  };

  return (
    <div className="fixed inset-0 z-[10000] flex items-center justify-center p-4">
      {/* Overlay */}
      <div
        className="absolute inset-0 bg-black/40"
        onClick={() => {
          if (rdvConflicts.length > 0) {
            setShowCloseWarning(true);
          } else {
            onClose();
          }
        }}
      />

      {/* Modal */}
      <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[80vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-background-200">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
              <i className="ri-alert-line text-amber-600 text-xl"></i>
            </div>
            <div>
              <h2 className="text-lg font-semibold text-foreground-950">
                Conflits de synchronisation
              </h2>
              <p className="text-sm text-foreground-600">
                {conflicts.length} conflit{conflicts.length > 1 ? 's' : ''} à résoudre
                {rdvConflicts.length > 0 && (
                  <span className="text-amber-600 font-medium">
                    {' — '}{rdvConflicts.length} rendez-vous concerné{rdvConflicts.length > 1 ? 's' : ''}
                  </span>
                )}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={() => {
              if (rdvConflicts.length > 0) {
                setShowCloseWarning(true);
              } else {
                onClose();
              }
            }}
            className="w-8 h-8 rounded-full hover:bg-background-100 flex items-center justify-center text-foreground-500 cursor-pointer transition-colors"
          >
            <i className="ri-close-line"></i>
          </button>
        </div>

        {/* Liste des conflits */}
        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
          {conflicts.length === 0 ? (
            <div className="text-center py-12">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-emerald-100 flex items-center justify-center">
                <i className="ri-check-line text-emerald-600 text-2xl"></i>
              </div>
              <p className="text-foreground-700 font-medium">Aucun conflit en attente</p>
              <p className="text-sm text-foreground-500 mt-1">Toutes les modifications ont été synchronisées avec succès.</p>
            </div>
          ) : (
            conflicts.map((conflict) => (
              <div
                key={conflict.mutationId}
                className="border border-background-200 rounded-lg p-4 bg-white hover:border-background-300 transition-colors"
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                      <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${CONFLICT_TYPE_COLORS[conflict.type] || 'bg-gray-100 text-gray-700'}`}>
                        {CONFLICT_TYPE_LABELS[conflict.type] || conflict.type}
                      </span>
                      <span className="text-xs text-foreground-500 bg-background-100 px-1.5 py-0.5 rounded">
                        {TABLE_LABELS[conflict.table] || conflict.table}
                      </span>
                      <span className="text-xs text-foreground-500">
                        {OPERATION_LABELS[conflict.operation] || conflict.operation}
                      </span>
                    </div>

                    <p className="text-sm text-foreground-700 leading-relaxed">
                      {conflict.description}
                    </p>

                    {conflict.serverMessage && (
                      <p className="text-xs text-foreground-500 mt-1.5 font-mono bg-background-50 px-2 py-1 rounded">
                        {conflict.serverMessage}
                      </p>
                    )}

                    {conflict.type === 'CROSS_DEVICE_CONFLICT' && conflict.serverData && (
                      <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded-md">
                        <p className="text-xs font-medium text-red-700 mb-1">Données actuelles sur le serveur :</p>
                        <pre className="text-xs text-red-600 overflow-x-auto max-h-24 font-mono">
                          {JSON.stringify(conflict.serverData, null, 1)}
                        </pre>
                        <p className="text-xs text-red-500 mt-1">
                          Utilisez « Forcer » pour écraser la version serveur avec vos modifications, ou « Ignorer » pour conserver la version serveur.
                        </p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Actions */}
                <div className="flex items-center gap-2 mt-3 pt-3 border-t border-background-100">
                  <button
                    type="button"
                    onClick={() => handleResolve(conflict.mutationId, 'RETRY')}
                    disabled={resolving[conflict.mutationId]}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-primary-700 bg-primary-50 hover:bg-primary-100 rounded-md transition-colors whitespace-nowrap cursor-pointer disabled:opacity-50"
                  >
                    <i className={`text-sm ${resolving[conflict.mutationId] ? 'ri-loader-4-line animate-spin' : 'ri-refresh-line'}`}></i>
                    Réessayer
                  </button>

                  <button
                    type="button"
                    onClick={() => handleResolve(conflict.mutationId, 'FORCE')}
                    disabled={resolving[conflict.mutationId]}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-amber-700 bg-amber-50 hover:bg-amber-100 rounded-md transition-colors whitespace-nowrap cursor-pointer disabled:opacity-50"
                  >
                    <i className="ri-flashlight-line text-sm"></i>
                    Forcer
                  </button>

                  <button
                    type="button"
                    onClick={() => handleResolve(conflict.mutationId, 'DISCARD')}
                    disabled={resolving[conflict.mutationId]}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-foreground-500 hover:text-red-600 hover:bg-red-50 rounded-md transition-colors whitespace-nowrap cursor-pointer disabled:opacity-50"
                  >
                    <i className="ri-delete-bin-line text-sm"></i>
                    Ignorer
                  </button>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Footer */}
        {conflicts.length > 0 && (
          <div className="px-6 py-3 border-t border-background-200 bg-background-50 flex items-center justify-between">
            <p className="text-xs text-foreground-500">
              Ces conflits nécessitent votre attention pour garantir l&apos;intégrité des données.
            </p>
            <button
              type="button"
              onClick={handleDismissAll}
              className="px-4 py-1.5 text-xs font-medium text-foreground-600 hover:text-foreground-900 bg-white border border-background-200 rounded-md hover:bg-background-50 transition-colors whitespace-nowrap cursor-pointer"
            >
              Tout ignorer
            </button>
          </div>
        )}

        {/* Avertissement fermeture avec conflits RDV non résolus */}
        {showCloseWarning && rdvConflicts.length > 0 && (
          <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/90 backdrop-blur-sm rounded-xl">
            <div className="text-center px-8 py-6 max-w-sm">
              <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-amber-100 flex items-center justify-center">
                <i className="ri-calendar-close-line text-amber-600 text-2xl"></i>
              </div>
              <h3 className="text-base font-semibold text-foreground-950 mb-2">
                Rendez-vous non confirmés
              </h3>
              <p className="text-sm text-foreground-600 mb-1">
                {rdvConflicts.length} rendez-vous n&apos;ont pas pu être confirmé{rdvConflicts.length > 1 ? 's' : ''}.
              </p>
              <p className="text-xs text-foreground-500 mb-6">
                Si vous fermez sans résoudre, le{rdvConflicts.length > 1 ? 's' : ''} patient{rdvConflicts.length > 1 ? 's' : ''} concerné{rdvConflicts.length > 1 ? 's' : ''} ne sera{rdvConflicts.length > 1 ? 'ont' : 'a'} pas informé{rdvConflicts.length > 1 ? 's' : ''} que son rendez-vous n&apos;est pas confirmé.
              </p>
              <div className="flex items-center justify-center gap-3">
                <button
                  type="button"
                  onClick={() => setShowCloseWarning(false)}
                  className="px-5 py-2 text-sm font-medium text-primary-700 bg-primary-50 hover:bg-primary-100 rounded-lg transition-colors whitespace-nowrap cursor-pointer"
                >
                  Résoudre d&apos;abord
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowCloseWarning(false);
                    onClose();
                  }}
                  className="px-5 py-2 text-sm font-medium text-foreground-500 hover:text-red-600 bg-background-100 hover:bg-red-50 rounded-lg transition-colors whitespace-nowrap cursor-pointer"
                >
                  Fermer quand même
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}